import { useCallback, useEffect, useState } from "react";
import {
  ActNatures,
  SurgerySidebarCategories,
} from "../../../classes/enums/SurgicalActsEnum";
import {
  generateSurgicalActKey,
  validateSurgicalActForKey,
} from "../../../classes/utils/SurgeryUtils";

const useAlignSurgicalActsState = ({
  dataBundle,
  currentPlan,
  currentPlanId,
  areViewsReady,
  setIsLoading,
  startSurgery,
  syncAllSurgicalActs,
  surgicalActsRefresh,
  plansRefresh,
  deleteSurgicalAct,
  performDecompensation,
  getInitialGlobalAlignment,
}) => {
  const [surgicalActs, setSurgicalActs] = useState({});
  const [globalAlignmentAct, setGlobalAlignmentAct] = useState(null);
  const [preOpGlobalAlignment, setPreOpGlobalAlignment] = useState(null);
  const [saasGlobalAlignment, setSaasGlobalAlignment] = useState(null);
  const [gapGlobalAlignment, setGapGlobalAlignment] = useState(null);

  const mergeReciprocalChangeAct = (incomingAct, existingAct) => {
    if (!incomingAct) return existingAct;

    const mergedAct = {
      actNature: ActNatures.ReciprocalChange,
      vertebra: incomingAct.vertebra || existingAct?.vertebra || null,
      value: incomingAct.value ?? existingAct?.value ?? null,
      spineRigidityType:
        incomingAct.spineRigidityType || existingAct?.spineRigidityType || null,
      warning: incomingAct.warning ?? existingAct?.warning ?? null,
    };

    if (existingAct?._id) {
      mergedAct._id = existingAct._id;
    }

    return mergedAct;
  };

  useEffect(() => {
    if (dataBundle.surgicalActs) {
      const actsMap = {};
      dataBundle.surgicalActs.forEach(act => {
        const actKey = generateSurgicalActKey(act);
        actsMap[actKey] = act;
      });
      setSurgicalActs(actsMap);
    }

    if (dataBundle.globalAlignmentAct?.length > 0) {
      setGlobalAlignmentAct(dataBundle.globalAlignmentAct[0]);
    } else {
      setGlobalAlignmentAct(null);
    }
  }, [dataBundle.surgicalActs, dataBundle.globalAlignmentAct]);

  const getGlobalAlignmentInfo = useCallback(async () => {
    try {
      const initialGlobalAlignment = await getInitialGlobalAlignment();

      if (initialGlobalAlignment) {
        const { preop, gap, saas } = initialGlobalAlignment;

        setPreOpGlobalAlignment({
          pelvicTilt: preop?.pelvic_tilt,
          kneeAngle: preop?.knee_angle,
          lumbarLordosis: preop?.lumbar_lordosis,
          sacralSlope: preop?.sacral_slope,
          globalTilt: preop?.global_tilt,
          pelvicIncidence: preop?.pelvic_incidence,
          targetPelvicTilt: preop?.target_pelvic_tilt,
          t1pa: preop?.t1pa,
          piLl: preop?.pi_ll,
          age: preop?.age,
        });

        setSaasGlobalAlignment({
          pelvicTilt: saas?.pelvic_tilt,
          kneeAngle: saas?.knee_angle,
          lumbarLordosis: saas?.lumbar_lordosis,
          sacralSlope: saas?.sacral_slope,
          pelvicIncidence: saas?.pelvic_incidence,
          targetPelvicTilt: saas?.target_pelvic_tilt,
          t1pa: saas?.t1pa,
          piLl: saas?.pi_ll,
          globalTilt: saas?.global_tilt,
          age: saas?.age,
        });

        setGapGlobalAlignment({
          pelvicTilt: gap?.pelvic_tilt,
          kneeAngle: gap?.knee_angle,
          lumbarLordosis: gap?.lumbar_lordosis,
          sacralSlope: gap?.sacral_slope,
          pelvicIncidence: gap?.pelvic_incidence,
          targetPelvicTilt: gap?.target_pelvic_tilt,
          t1pa: gap?.t1pa,
          piLl: gap?.pi_ll,
          age: gap?.age,
          globalTilt: gap?.global_tilt,
        });
      }
    } catch (error) {}
  }, [getInitialGlobalAlignment]);

  const addSurgicalActs = useCallback(
    async (
      surgicalActsToAdd,
      { baseSurgicalActs, onCloseToolWidget, onFinally } = {}
    ) => {
      if (!areViewsReady || !currentPlan?._id) {
        return;
      }

      const actsArray = Array.isArray(surgicalActsToAdd)
        ? surgicalActsToAdd
        : [surgicalActsToAdd];

      for (const act of actsArray) {
        if (!validateSurgicalActForKey(act)) {
          return;
        }
      }

      setIsLoading(true);
      try {
        let updatedSurgicalActs = {
          ...(baseSurgicalActs ? baseSurgicalActs : surgicalActs),
        };

        actsArray.forEach(surgicalAct => {
          const actKey = generateSurgicalActKey(surgicalAct);
          const existingAct = updatedSurgicalActs[actKey];
          const isNewAct = !existingAct;

          const actToAdd = {
            ...surgicalAct,
            ...(existingAct && { _id: existingAct._id }),
            ...(isNewAct && { _new: true }),
            ...(!isNewAct && { _modified: true }),
          };

          updatedSurgicalActs[actKey] = actToAdd;
        });

        setSurgicalActs(updatedSurgicalActs);

        const surgicalActsArray = Object.values(updatedSurgicalActs);
        await startSurgery(surgicalActsArray);
        const globalAlignmentArray = globalAlignmentAct
          ? [globalAlignmentAct]
          : [];

        const createdActsWithIds = await syncAllSurgicalActs(
          surgicalActsArray,
          globalAlignmentArray,
          currentPlan._id
        );

        surgicalActsRefresh();
        await plansRefresh?.();

        if (createdActsWithIds && createdActsWithIds.length > 0) {
          const updatedActsWithIds = { ...updatedSurgicalActs };

          createdActsWithIds.forEach(({ originalAct, newId }) => {
            const actKey = generateSurgicalActKey(originalAct);
            if (updatedActsWithIds[actKey]) {
              updatedActsWithIds[actKey] = {
                ...updatedActsWithIds[actKey],
                _id: newId,
              };
              delete updatedActsWithIds[actKey]._new;
              delete updatedActsWithIds[actKey]._added;
            }
          });

          setSurgicalActs(updatedActsWithIds);
        } else {
          const cleanedActs = { ...updatedSurgicalActs };
          Object.keys(cleanedActs).forEach(key => {
            delete cleanedActs[key]._new;
            delete cleanedActs[key]._modified;
          });
          setSurgicalActs(cleanedActs);
        }

        onCloseToolWidget?.();
      } catch (error) {
        setSurgicalActs(baseSurgicalActs ? baseSurgicalActs : surgicalActs);
      } finally {
        setIsLoading(false);
        onFinally?.(SurgerySidebarCategories.history);
      }
    },
    [
      areViewsReady,
      currentPlan?._id,
      surgicalActs,
      startSurgery,
      globalAlignmentAct,
      syncAllSurgicalActs,
      surgicalActsRefresh,
      plansRefresh,
      setIsLoading,
    ]
  );

  const getRodActKeys = useCallback(acts => {
    return new Set(
      (acts || [])
        .filter(act => act.actNature === ActNatures.Rod)
        .map(generateSurgicalActKey)
    );
  }, []);

  const updateSurgicalActs = useCallback(
    async (
      surgicalActsToUpdate,
      { onCloseToolWidget, onFinally, canApplyReciprocalChange } = {}
    ) => {
      if (!areViewsReady || !currentPlan?._id) {
        return;
      }

      const actsArray = Array.isArray(surgicalActsToUpdate)
        ? surgicalActsToUpdate
        : [surgicalActsToUpdate];

      for (const act of actsArray) {
        if (!validateSurgicalActForKey(act)) {
          return;
        }
      }

      const incomingRodKeys = getRodActKeys(actsArray);
      const currentRodActs = Object.values(surgicalActs).filter(
        act => act.actNature === ActNatures.Rod
      );

      let baseSurgicalActs = { ...surgicalActs };

      // Delete rod acts that are no longer in the incoming array
      for (const rodAct of currentRodActs) {
        const rodKey = generateSurgicalActKey(rodAct);
        if (!incomingRodKeys.has(rodKey)) {
          delete baseSurgicalActs[rodKey];
          if (rodAct._id) {
            await deleteSurgicalAct(rodAct);
          }
        }
      }

      const incomingReciprocalIndex = actsArray.findIndex(
        act => act.actNature === ActNatures.ReciprocalChange
      );
      const existingReciprocalChangeAct = Object.values(baseSurgicalActs).find(
        act => act.actNature === ActNatures.ReciprocalChange
      );

      // Enforce canApplyReciprocalChange as the source of truth
      if (canApplyReciprocalChange === false) {
        if (incomingReciprocalIndex !== -1) {
          actsArray.splice(incomingReciprocalIndex, 1);
        }
        if (existingReciprocalChangeAct) {
          const rcKey = generateSurgicalActKey(existingReciprocalChangeAct);
          delete baseSurgicalActs[rcKey];
          if (existingReciprocalChangeAct._id) {
            await deleteSurgicalAct(existingReciprocalChangeAct);
          }
        }
      } else {
        // If caller sends RC, replace/merge; if not, drop existing to mirror other acts behavior
        if (incomingReciprocalIndex !== -1) {
          // Merge incoming with existing (preserves _id so addSurgicalActs marks it as modified)
          actsArray[incomingReciprocalIndex] = mergeReciprocalChangeAct(
            actsArray[incomingReciprocalIndex],
            existingReciprocalChangeAct
          );
          // Don't delete from baseSurgicalActs - let addSurgicalActs handle the replacement
        } else if (existingReciprocalChangeAct) {
          // No incoming RC but existing one exists - delete it
          const rcKey = generateSurgicalActKey(existingReciprocalChangeAct);
          delete baseSurgicalActs[rcKey];
          if (existingReciprocalChangeAct._id) {
            await deleteSurgicalAct(existingReciprocalChangeAct);
          }
        }
      }

      await addSurgicalActs(actsArray, {
        baseSurgicalActs,
        onCloseToolWidget,
        onFinally,
      });
    },
    [
      addSurgicalActs,
      areViewsReady,
      currentPlan?._id,
      deleteSurgicalAct,
      getRodActKeys,
      mergeReciprocalChangeAct,
      surgicalActs,
    ]
  );

  const deleteSurgicalActEntry = useCallback(
    async surgicalActToDelete => {
      if (!areViewsReady || !currentPlan?._id) {
        return;
      }

      if (!validateSurgicalActForKey(surgicalActToDelete)) {
        return;
      }

      setIsLoading(true);

      try {
        await deleteSurgicalAct(surgicalActToDelete);
        const actKey = generateSurgicalActKey(surgicalActToDelete);
        const updatedSurgicalActs = { ...surgicalActs };

        if (!updatedSurgicalActs[actKey]) {
          setIsLoading(false);
          return;
        }

        delete updatedSurgicalActs[actKey];
        setSurgicalActs(updatedSurgicalActs);

        const surgicalActsArray = Object.values(updatedSurgicalActs);
        await startSurgery(surgicalActsArray);
        surgicalActsRefresh();
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    },
    [
      areViewsReady,
      currentPlan?._id,
      deleteSurgicalAct,
      surgicalActs,
      startSurgery,
      surgicalActsRefresh,
      setIsLoading,
    ]
  );

  const bulkDeleteSurgicalActs = useCallback(
    async surgicalActsToDelete => {
      if (!areViewsReady || !currentPlan?._id) {
        return;
      }

      if (
        !Array.isArray(surgicalActsToDelete) ||
        surgicalActsToDelete.length === 0
      ) {
        return;
      }

      setIsLoading(true);

      try {
        const deletionPromises = surgicalActsToDelete.map(async surgicalAct => {
          if (surgicalAct._id) {
            await deleteSurgicalAct(surgicalAct);
          }
        });

        await Promise.all(deletionPromises);
        const updatedSurgicalActs = { ...surgicalActs };
        surgicalActsToDelete.forEach(surgicalAct => {
          if (!validateSurgicalActForKey(surgicalAct)) {
            return;
          }

          const actKey = generateSurgicalActKey(surgicalAct);
          if (updatedSurgicalActs[actKey]) {
            delete updatedSurgicalActs[actKey];
          }
        });

        // If rods are being deleted, check if we should also delete reciprocal change
        const deletingRods = surgicalActsToDelete.some(
          act => act.actNature === ActNatures.Rod
        );
        if (deletingRods) {
          const remainingRods = Object.values(updatedSurgicalActs).filter(
            act => act.actNature === ActNatures.Rod
          );
          // If no rods remain, delete reciprocal change act
          if (remainingRods.length === 0) {
            const reciprocalChangeAct = Object.values(updatedSurgicalActs).find(
              act => act.actNature === ActNatures.ReciprocalChange
            );
            if (reciprocalChangeAct) {
              const rcKey = generateSurgicalActKey(reciprocalChangeAct);
              delete updatedSurgicalActs[rcKey];
              if (reciprocalChangeAct._id) {
                await deleteSurgicalAct(reciprocalChangeAct);
              }
            }
          }
        }

        setSurgicalActs(updatedSurgicalActs);

        const remainingSurgicalActsArray = Object.values(updatedSurgicalActs);
        await startSurgery(remainingSurgicalActsArray);
        surgicalActsRefresh();
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    },
    [
      areViewsReady,
      currentPlan?._id,
      deleteSurgicalAct,
      surgicalActs,
      startSurgery,
      surgicalActsRefresh,
      setIsLoading,
    ]
  );

  const deleteGlobalAlignmentEntry = useCallback(
    async globalAlignmentToDelete => {
      if (!areViewsReady || !currentPlan?._id) {
        return;
      }

      setIsLoading(true);

      try {
        if (globalAlignmentToDelete._id) {
          await deleteSurgicalAct(globalAlignmentToDelete);
        }

        setGlobalAlignmentAct(null);

        await performDecompensation(null, null);

        const surgicalActsArray = Object.values(surgicalActs);
        if (surgicalActsArray.length > 0) {
          await startSurgery(surgicalActsArray);
        }

        surgicalActsRefresh();
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    },
    [
      areViewsReady,
      currentPlan?._id,
      deleteSurgicalAct,
      performDecompensation,
      surgicalActs,
      startSurgery,
      surgicalActsRefresh,
      setIsLoading,
    ]
  );

  const manualGlobalAlignment = useCallback(
    async (
      globalAlignmentActNew,
      { onCloseToolWidget, isOverlayToolsActive } = {}
    ) => {
      if (!currentPlan?._id || !areViewsReady) {
        return;
      }

      setIsLoading(true);
      try {
        await performDecompensation(
          globalAlignmentActNew.pelvicTilt,
          globalAlignmentActNew.kneeAngle,
          globalAlignmentActNew.method,
          globalAlignmentActNew.lumbarLordosis,
          globalAlignmentActNew.sacralSlope,
          globalAlignmentActNew.globalTilt,
          globalAlignmentActNew.t1pa,
          globalAlignmentActNew.targetPelvicTilt,
          globalAlignmentActNew.piLl
        );

        const actToAdd = {
          ...globalAlignmentActNew,
          ...(globalAlignmentAct && { _id: globalAlignmentAct._id }),
          ...(!globalAlignmentAct && { _new: true }),
          ...(globalAlignmentAct && { _modified: true }),
        };

        const surgicalActsArray = Object.values(surgicalActs);
        if (surgicalActsArray.length > 0) {
          await startSurgery(surgicalActsArray);
        }

        const createdActsWithIds = await syncAllSurgicalActs(
          surgicalActsArray,
          [actToAdd],
          currentPlan._id
        );

        await plansRefresh?.();
        if (!globalAlignmentAct && createdActsWithIds?.length > 0) {
          const globalAlignmentWithId = createdActsWithIds.find(
            ({ originalAct }) =>
              originalAct.actNature === ActNatures.GlobalAlignment
          );

          if (globalAlignmentWithId) {
            setGlobalAlignmentAct({
              ...actToAdd,
              _id: globalAlignmentWithId.newId,
              _new: undefined,
              _modified: undefined,
            });
          }
        } else {
          setGlobalAlignmentAct({
            ...actToAdd,
            _new: undefined,
            _modified: undefined,
          });
        }

        surgicalActsRefresh();
        onCloseToolWidget?.();
      } catch (error) {
        if (!globalAlignmentAct) {
          setGlobalAlignmentAct(null);
        }
      } finally {
        setIsLoading(false);
        if (!isOverlayToolsActive) {
          onCloseToolWidget?.();
        }
      }
    },
    [
      currentPlan?._id,
      areViewsReady,
      performDecompensation,
      globalAlignmentAct,
      surgicalActs,
      syncAllSurgicalActs,
      surgicalActsRefresh,
      plansRefresh,
      setIsLoading,
      startSurgery,
    ]
  );

  return {
    surgicalActs,
    globalAlignmentAct,
    preOpGlobalAlignment,
    saasGlobalAlignment,
    gapGlobalAlignment,
    setGlobalAlignmentAct,
    getGlobalAlignmentInfo,
    addSurgicalActs,
    updateSurgicalActs,
    deleteSurgicalActEntry,
    bulkDeleteSurgicalActs,
    deleteGlobalAlignmentEntry,
    manualGlobalAlignment,
  };
};

export default useAlignSurgicalActsState;
